/**
 * Scalar formatting for the deterministic water-use screen's reader text.
 *
 * The screen stores full-precision model values (`IT load 423.7288 MW`,
 * `PUE 1.18-1.18`, `utilization 0.5-1`). These helpers give each quantity one
 * display precision and one range style:
 *   - en dash between ends, unit once after the high end;
 *   - a range whose ends DISPLAY identically collapses to one value
 *     (`PUE 1.18`, never `PUE 1.18–1.18`);
 *   - PUE 2 decimals; percent and hours whole numbers; °C 1 decimal;
 *   - a recorded MW scope prints exactly as recorded, and a MW figure derived
 *     from it prints any endpoint equal to the recorded scope the same way
 *     (other derived MW scale their decimals to magnitude).
 *
 * Water volumes are NOT formatted here: they go through `../waterRateFormat`,
 * whose output forms are the ones the WATER-TRUTH-1 guard accepts as exact
 * screen endpoints. These scalars carry no water unit, so the guard never
 * reads them as a water quantity.
 */

export interface ScalarRange { low: number; high: number }

const formatter = (fractionDigits: number): Intl.NumberFormat => new Intl.NumberFormat('en-US', {
  minimumFractionDigits: fractionDigits,
  maximumFractionDigits: fractionDigits,
});

const WHOLE = formatter(0);
const ONE_DECIMAL = formatter(1);
const TWO_DECIMALS = formatter(2);

function range(value: ScalarRange, format: (value: number) => string, unit = ''): string {
  const low = format(value.low);
  const high = format(value.high);
  const suffix = unit ? (unit === '%' ? unit : ` ${unit}`) : '';
  return low === high ? `${low}${suffix}` : `${low}–${high}${suffix}`;
}

/** `PUE 1.25` / `PUE 1.18–1.30`. */
export function formatPueRange(value: ScalarRange): string {
  return `PUE ${range(value, v => TWO_DECIMALS.format(v))}`;
}

/** A 0–1 fraction range as whole percent: `50–100%`. */
export function formatFractionPercentRange(value: ScalarRange): string {
  return range({ low: value.low * 100, high: value.high * 100 }, v => WHOLE.format(v), '%');
}

/** One recorded MW value exactly as the prompt's SCOPE LOCK prints it. */
function recordedMw(value: number): string {
  return value.toLocaleString('en-US');
}

/**
 * The RECORDED load scope, printed exactly as the prompt's SCOPE LOCK prints it
 * (`reportPrompts.ts` `recordedWaterUseScreenScope`: `toLocaleString('en-US')`).
 * Rounding a recorded scope such as 12.34 MW to 12 MW would invite prose to
 * restate a load the guard's MW-scope check then reads as out of scope.
 */
export function formatRecordedMwScope(value: ScalarRange): string {
  const low = Math.min(value.low, value.high);
  const high = Math.max(value.low, value.high);
  return low === high
    ? `${recordedMw(low)} MW`
    : `${recordedMw(low)}–${recordedMw(high)} MW`;
}

/** The screen stores derived MW at 4 dp (`roundedBand(..., 4)`); compare at that precision. */
const DERIVED_MW_STORED_DECIMALS = 4;

function sameStoredMw(a: number, b: number): boolean {
  const scale = 10 ** DERIVED_MW_STORED_DECIMALS;
  return Math.round(a * scale) === Math.round(b * scale);
}

/** Display decimals for a derived MW value: 0 at 100 MW and above, 1 from 10 MW, 2 below. */
function derivedMwDecimals(value: number): number {
  const magnitude = Math.abs(value);
  if (magnitude >= 100) return 0;
  if (magnitude >= 10) return 1;
  return 2;
}

function derivedMw(value: number, recorded: ScalarRange): string {
  const low = Math.min(recorded.low, recorded.high);
  const high = Math.max(recorded.low, recorded.high);
  // An endpoint that IS a recorded endpoint prints exactly as the header does,
  // so the section never states one load two ways (`37.5 MW` beside `38 MW`).
  if (sameStoredMw(value, low)) return recordedMw(low);
  if (sameStoredMw(value, high)) return recordedMw(high);
  // A derived value inside the recorded interval must not round outside it.
  const inside = value >= low && value <= high;
  for (let decimals = derivedMwDecimals(value); ; decimals += 1) {
    const text = new Intl.NumberFormat('en-US', { maximumFractionDigits: decimals }).format(value);
    const shown = Number(text.replace(/,/g, ''));
    if (!inside || decimals >= DERIVED_MW_STORED_DECIMALS || (shown >= low && shown <= high)) return text;
  }
}

/**
 * A MW range derived from the recorded load scope (IT load, facility heat,
 * dry-cooling duty). An endpoint equal to a recorded endpoint prints exactly as
 * `formatRecordedMwScope` prints it; any other endpoint prints at a precision
 * scaled to its magnitude (`424–847 MW`, `32.6 MW`, `0.42–2.54 MW`), and one
 * inside the recorded interval never rounds outside it. Whole-MW rounding of
 * these figures stated a recorded 37.5 MW load as `38 MW` next to a `37.5 MW`
 * header (a blocking R-67 internal-number contradiction), and printed a 0.4 MW
 * IT load as `0 MW`.
 */
export function formatDerivedMwRange(value: ScalarRange, recorded: ScalarRange): string {
  const low = derivedMw(Math.min(value.low, value.high), recorded);
  const high = derivedMw(Math.max(value.low, value.high), recorded);
  return low === high ? `${low} MW` : `${low}–${high} MW`;
}

/** `41.3 °C`. */
export function formatCelsius(value: number): string {
  return `${ONE_DECIMAL.format(value)} °C`;
}

/** `29.3–32.2 °C`. */
export function formatCelsiusRange(value: ScalarRange): string {
  return range(value, v => ONE_DECIMAL.format(v), '°C');
}

/** A whole-number count range with a trailing unit: `1,025–1,793 hours/year`. */
export function formatWholeRange(value: ScalarRange, unit: string): string {
  return range(value, v => WHOLE.format(v), unit);
}

/** A whole-number count: `8,760`. */
export function formatWholeNumber(value: number): string {
  return WHOLE.format(value);
}

const COUNT_WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'] as const;

/** Small counts as words (`five`), larger as digits. */
export function countWord(count: number): string {
  return Number.isInteger(count) && count >= 0 && count < COUNT_WORDS.length
    ? COUNT_WORDS[count]!
    : WHOLE.format(count);
}

/** `a`, `a and b`, `a, b and c`. */
export function joinWithAnd(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** Upper-case the first character only. */
export function capitalizeFirst(text: string): string {
  return text ? `${text[0]!.toUpperCase()}${text.slice(1)}` : text;
}
